/* eslint-disable no-nested-ternary */
import JsPDF from 'jspdf';
// eslint-disable-next-line import/no-extraneous-dependencies -- listed in package.json
import autoTable from 'jspdf-autotable';

import type { ExportHeaderInfo } from './types';

export const MERGED_CELL_STYLES = {
  valign: 'middle' as const,
  halign: 'center' as const,
};

export const PDF_TABLE_OPTIONS = {
  styles: {
    fontSize: 8,
    cellPadding: 3,
    overflow: 'linebreak' as const,
    halign: 'center' as const,
  },
  headStyles: {
    fillColor: [220, 230, 241] as [number, number, number],
    textColor: [0, 0, 0] as [number, number, number],
    fontStyle: 'bold' as const,
  },
  bodyStyles: { valign: 'middle' as const },
  theme: 'grid' as const,
};

export type PdfCell =
  | string
  | { content: string; rowSpan?: number; styles?: object };

const TITLE_FONT_SIZE = 14;
const SUB_FONT_SIZE = 10;
const LINE_HEIGHT = 7;
/** Max width for wrapped text (landscape A4 ~297mm minus margins) */
const PDF_TEXT_MAX_WIDTH = 265;

export const createPdfTableBlob = (
  head: string[],
  body: PdfCell[][],
  filename: string,
  headerInfo?: ExportHeaderInfo,
): { blob: Blob; filename: string } => {
  const doc = new JsPDF({ orientation: 'landscape' });
  let startY = 10;

  if (headerInfo) {
    const {
      projectName,
      companyName,
      companyAddress,
      drawnBy,
      revisionNumber,
      projectNumber,
      responsibleEngineer,
      description,
      reportTitle,
    } = headerInfo;
    doc.setFontSize(TITLE_FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.text(projectName, 14, startY);
    startY += LINE_HEIGHT;
    doc.setFontSize(SUB_FONT_SIZE);
    const drawLabelValue = (label: string, value: string) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, startY);
      const labelW = doc.getTextWidth(`${label}: `);
      doc.setFont('helvetica', 'normal');
      const valueLines = doc.splitTextToSize(
        value,
        PDF_TEXT_MAX_WIDTH - labelW,
      );
      if (valueLines.length > 0) {
        doc.text(valueLines[0], 14 + labelW, startY);
        for (let i = 1; i < valueLines.length; i += 1) {
          doc.text(valueLines[i], 14, startY + i * LINE_HEIGHT);
        }
        startY += valueLines.length * LINE_HEIGHT;
      } else {
        startY += LINE_HEIGHT;
      }
    };
    if (companyName) drawLabelValue('Company', companyName);
    if (companyAddress) drawLabelValue('Address', companyAddress);
    if (drawnBy) drawLabelValue('Drawn by', drawnBy);
    if (revisionNumber) drawLabelValue('Revision', revisionNumber);
    if (projectNumber) drawLabelValue('Project number', projectNumber);
    if (responsibleEngineer)
      drawLabelValue('Responsible engineer', responsibleEngineer);
    if (description) drawLabelValue('Description', description);

    // Extra vertical space between project data and table title
    startY += LINE_HEIGHT;

    doc.setFont('helvetica', 'bold');
    doc.text(reportTitle, 14, startY);
    startY += LINE_HEIGHT + 4;
  }

  autoTable(doc, {
    head: [head],
    body,
    startY,
    ...PDF_TABLE_OPTIONS,
  });
  return { blob: doc.output('blob'), filename: `${filename}.pdf` };
};

export const formatIp = (ip: { ip: string; subnetMask: string }) =>
  ip.ip && ip.ip.trim()
    ? ip.subnetMask && ip.subnetMask.trim()
      ? `${ip.ip} (${ip.subnetMask})`
      : ip.ip
    : '';
